// src/pages/Administrators.jsx
//
// Gestion des comptes administrateurs (création, changement de niveau,
// activation/suspension/révocation). Toutes les actions d'écriture sont
// réservées au Super Admin côté Worker (RBAC vérifié côté serveur).

import React, { useState, useEffect, useMemo } from 'react';
import {
  UserPlus,
  ShieldAlert,
  RefreshCw,
  X,
  CheckCircle2,
  Users,
  MoreVertical,
  Shield,
  Mail,
  UserCheck,
  UserX,
  KeyRound,
  Layers,
} from 'lucide-react';
import {
  PageHeader,
  StatusBadge,
  DonutChart,
  HorizontalBars,
  EmptyState,
} from '../components/ui/UIComponents';
import * as adminApi from '../services/adminAdministrateurs';
import RolesPermissionsTab from '../components/admin/RolesPermissionsTab';
import SessionsTab from '../components/admin/SessionsTab';
import { useI18n } from '../i18n/useI18n';

const CHART_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#f43f5e', '#0ea5e9', '#8b5cf6', '#64748b'];

function initialsFromName(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || 'A';
}

export default function Administrators() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('comptes');
  const [admins, setAdmins] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [rolesCount, setRolesCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [niveauFilter, setNiveauFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [adminsRes, niveauxRes, rolesRes] = await Promise.all([
        adminApi.getAdministrateurs(),
        adminApi.getNiveauxAutorite(),
        adminApi.getRoles().catch(() => ({ roles: [] })),
      ]);
      setAdmins(adminsRes.administrateurs || []);
      setNiveaux(niveauxRes.niveaux || []);
      setRolesCount((rolesRes.roles || []).length);
    } catch (err) {
      setError(err.message || 'Impossible de charger les administrateurs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChangeStatut = async (admin, statut) => {
    setOpenMenuId(null);
    const libelles = { actif: 'réactiver', suspendu: 'suspendre', revoque: 'révoquer' };
    if (!window.confirm(`Confirmez-vous vouloir ${libelles[statut]} ${admin.nom_complet} ?`)) return;

    setError(null);
    setSuccess(null);
    try {
      await adminApi.changerStatutAdministrateur(admin.id, statut);
      setSuccess(`${admin.nom_complet} : statut mis à jour.`);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChangeNiveau = async (admin, niveauAutoriteId) => {
    setOpenMenuId(null);
    setError(null);
    setSuccess(null);
    try {
      await adminApi.changerNiveauAdministrateur(admin.id, Number(niveauAutoriteId));
      setSuccess(`${admin.nom_complet} : niveau d'autorité mis à jour.`);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const renderStatut = (statut) => {
    switch (statut) {
      case 'actif':
        return <StatusBadge status="succes" text="Actif" />;
      case 'suspendu':
        return <StatusBadge status="en_attente" text="Suspendu" />;
      case 'revoque':
        return <StatusBadge status="echec" text="Révoqué" />;
      default:
        return <StatusBadge status="neutre" text={statut || 'Inconnu'} />;
    }
  };

  const niveauxAttribuables = niveaux.filter((n) => n.code !== 'SUPER_ADMIN');

  const kpis = useMemo(() => {
    const total = admins.length;
    const actifs = admins.filter((a) => a.statut === 'actif').length;
    const inactifs = total - actifs;
    return { total, actifs, inactifs };
  }, [admins]);

  const statusSegments = useMemo(() => {
    const counts = {};
    admins.forEach((a) => {
      const s = a.statut || 'inconnu';
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value], i) => ({
      label,
      value,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
  }, [admins]);

  const niveauBars = useMemo(() => {
    const counts = {};
    admins.forEach((a) => {
      const label = a.niveau_libelle || a.niveau_code || 'Non défini';
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value]) => ({
      label,
      value,
      color: 'bg-indigo-500',
    }));
  }, [admins]);

  const filteredAdmins = useMemo(() => {
    return admins.filter((a) => {
      const matchSearch =
        !search ||
        (a.nom_complet && a.nom_complet.toLowerCase().includes(search.toLowerCase())) ||
        (a.email && a.email.toLowerCase().includes(search.toLowerCase()));
      const matchStatus = statusFilter === 'ALL' || a.statut === statusFilter;
      const matchNiveau =
        niveauFilter === 'ALL' ||
        a.niveau_code === niveauFilter ||
        a.niveau_libelle === niveauFilter;
      return matchSearch && matchStatus && matchNiveau;
    });
  }, [admins, search, statusFilter, niveauFilter]);

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t("pages.administrators.title")}
        subtitle={t("pages.administrators.subtitle")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
              <Shield size={12} /> Gouvernance
            </span>
            <button onClick={fetchData} className="dsi-btn-secondary text-xs">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Actualiser
            </button>
            <button onClick={() => setShowCreateForm(true)} className="dsi-btn-primary text-xs">
              <UserPlus size={16} /> Nouvel administrateur
            </button>
          </div>
        }
      />

      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('comptes')}
          className={`px-4 py-2.5 text-xs font-bold border-b-2 -mb-px transition-colors ${
            activeTab === 'comptes'
              ? 'border-indigo-600 text-indigo-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Comptes
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-4 py-2.5 text-xs font-bold border-b-2 -mb-px transition-colors ${
            activeTab === 'roles'
              ? 'border-indigo-600 text-indigo-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Rôles & Permissions
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          className={`px-4 py-2.5 text-xs font-bold border-b-2 -mb-px transition-colors ${
            activeTab === 'sessions'
              ? 'border-indigo-600 text-indigo-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Sessions
        </button>
      </div>

      {activeTab === 'roles' ? (
        <RolesPermissionsTab admins={admins} />
      ) : activeTab === 'sessions' ? (
        <SessionsTab admins={admins} />
      ) : (
        <>
          {error && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-800 text-xs font-bold flex justify-between items-center">
              <span className="flex items-center gap-2">
                <ShieldAlert size={16} /> {error}
              </span>
              <button onClick={fetchData} className="dsi-btn-secondary text-xs">
                Réessayer
              </button>
            </div>
          )}

          {success && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-800 text-xs font-bold flex items-center gap-2">
              <CheckCircle2 size={16} /> {success}
            </div>
          )}

          {showCreateForm && (
            <CreateAdminModal
              niveaux={niveauxAttribuables}
              onClose={() => setShowCreateForm(false)}
              onCreated={(msg) => {
                setShowCreateForm(false);
                setSuccess(msg);
                fetchData();
              }}
              onError={(msg) => setError(msg)}
            />
          )}

          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <div className="dsi-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Administrateurs</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{loading ? '…' : kpis.total}</p>
                </div>
                <Users size={16} className="text-indigo-600" />
              </div>
            </div>
            <div className="dsi-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Actifs</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{loading ? '…' : kpis.actifs}</p>
                </div>
                <UserCheck size={16} className="text-emerald-600" />
              </div>
            </div>
            <div className="dsi-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Inactifs</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{loading ? '…' : kpis.inactifs}</p>
                </div>
                <UserX size={16} className="text-amber-600" />
              </div>
            </div>
            <div className="dsi-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Rôles configurés</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {rolesCount === null ? '…' : rolesCount}
                  </p>
                </div>
                <Layers size={16} className="text-violet-600" />
              </div>
            </div>
            <div className="dsi-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Niveaux d’autorité</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{niveaux.length || '—'}</p>
                </div>
                <KeyRound size={16} className="text-blue-600" />
              </div>
            </div>
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Statut des comptes</h3>
              <p className="text-xs text-slate-500 mb-4">Répartition calculée sur les administrateurs reçus.</p>
              {statusSegments.length ? (
                <DonutChart
                  segments={statusSegments}
                  centerValue={kpis.total}
                  centerLabel="Admins"
                />
              ) : (
                <EmptyState title="Aucune donnée" message="Aucun administrateur pour construire le graphique." />
              )}
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Niveaux d’autorité</h3>
              <p className="text-xs text-slate-500 mb-4">Nombre d’administrateurs par niveau réellement observé.</p>
              {niveauBars.length ? (
                <HorizontalBars rows={niveauBars} />
              ) : (
                <EmptyState title="Aucun niveau observé" message="Les niveaux apparaîtront dès réception des comptes." />
              )}
            </div>
          </div>

          {/* Cartes admins synthétiques */}
          {!loading && admins.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredAdmins.slice(0, 6).map((admin) => (
                <div key={`card-${admin.id}`} className="dsi-card p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">
                      {initialsFromName(admin.nom_complet)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{admin.nom_complet}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
                        <Mail size={11} /> {admin.email}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {admin.niveau_libelle || admin.niveau_code || '—'}
                        </span>
                        {renderStatut(admin.statut)}
                        {admin.niveau_code === 'SUPER_ADMIN' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                            Compte protégé
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-2">
                        Créé le{' '}
                        {admin.created_at
                          ? new Date(admin.created_at).toLocaleDateString('fr-FR')
                          : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filtres + tableau */}
          <div className="dsi-card p-4 flex flex-col lg:flex-row gap-3 items-center justify-between">
            <input
              className="dsi-input lg:max-w-sm"
              placeholder="Rechercher nom ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex flex-wrap gap-2 w-full lg:w-auto">
              <select className="dsi-input w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="ALL">Tous les statuts</option>
                <option value="actif">Actifs</option>
                <option value="suspendu">Suspendus</option>
                <option value="revoque">Révoqués</option>
              </select>
              <select className="dsi-input w-auto" value={niveauFilter} onChange={(e) => setNiveauFilter(e.target.value)}>
                <option value="ALL">Tous les niveaux</option>
                {niveaux.map((n) => (
                  <option key={n.id} value={n.code || n.libelle}>
                    {n.libelle}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center dsi-card">
              <RefreshCw size={24} className="animate-spin text-indigo-600 mx-auto" />
              <p className="text-xs font-bold text-slate-600 mt-2">Chargement des administrateurs...</p>
            </div>
          ) : admins.length === 0 && !error ? (
            <EmptyState
              title="Aucun administrateur trouvé"
              message="Créez le premier compte pour démarrer la gouvernance du portail."
              onRetry={fetchData}
            />
          ) : filteredAdmins.length === 0 ? (
            <EmptyState title="Aucun résultat" message="Aucun administrateur ne correspond aux filtres." />
          ) : (
            <div className="dsi-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-xs font-bold border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4">Administrateur</th>
                      <th className="px-6 py-4">Niveau d’autorité</th>
                      <th className="px-6 py-4">Statut</th>
                      <th className="px-6 py-4">Créé le</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredAdmins.map((admin) => (
                      <tr key={admin.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">
                              {initialsFromName(admin.nom_complet)}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900">{admin.nom_complet}</div>
                              <div className="text-xs text-slate-500 flex items-center gap-1">
                                <Mail size={11} /> {admin.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1.5">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                              {admin.niveau_libelle}
                            </span>
                            {admin.niveau_code === 'SUPER_ADMIN' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                                Compte protégé
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">{renderStatut(admin.statut)}</td>
                        <td className="px-6 py-4 text-slate-500 text-xs">
                          {admin.created_at
                            ? new Date(admin.created_at).toLocaleDateString('fr-FR')
                            : '—'}
                        </td>
                        <td className="px-6 py-4 text-right relative">
                          {admin.niveau_code === 'SUPER_ADMIN' ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                              Non modifiable · Compte protégé
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() => setOpenMenuId(openMenuId === admin.id ? null : admin.id)}
                                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                              >
                                <MoreVertical size={16} />
                              </button>
                              {openMenuId === admin.id && (
                                <div className="absolute right-6 top-12 z-10 w-56 bg-white border border-slate-200 rounded-2xl shadow-xl py-2 text-left">
                                  <p className="px-4 py-1 text-[10px] font-bold text-slate-400 uppercase">
                                    Changer le niveau
                                  </p>
                                  {niveauxAttribuables.map((n) => (
                                    <button
                                      key={n.id}
                                      onClick={() => handleChangeNiveau(admin, n.id)}
                                      className="w-full text-left px-4 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                                    >
                                      {n.libelle}
                                    </button>
                                  ))}
                                  <div className="my-1 border-t border-slate-100" />
                                  {admin.statut !== 'actif' && (
                                    <button
                                      onClick={() => handleChangeStatut(admin, 'actif')}
                                      className="w-full text-left px-4 py-1.5 text-xs text-emerald-700 hover:bg-emerald-50"
                                    >
                                      Réactiver
                                    </button>
                                  )}
                                  {admin.statut !== 'suspendu' && (
                                    <button
                                      onClick={() => handleChangeStatut(admin, 'suspendu')}
                                      className="w-full text-left px-4 py-1.5 text-xs text-amber-700 hover:bg-amber-50"
                                    >
                                      Suspendre
                                    </button>
                                  )}
                                  {admin.statut !== 'revoque' && (
                                    <button
                                      onClick={() => handleChangeStatut(admin, 'revoque')}
                                      className="w-full text-left px-4 py-1.5 text-xs text-rose-700 hover:bg-rose-50"
                                    >
                                      Révoquer
                                    </button>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 bg-slate-50/70 text-xs text-slate-500 border-t border-slate-100">
                Total : {filteredAdmins.length} administrateur(s) affiché(s) sur {admins.length}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CreateAdminModal({ niveaux, onClose, onCreated, onError }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nomComplet, setNomComplet] = useState('');
  const [niveauId, setNiveauId] = useState(niveaux[0]?.id || '');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !nomComplet || !niveauId) {
      setLocalError('Tous les champs sont requis.');
      return;
    }
    if (password.length < 8) {
      setLocalError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    setSubmitting(true);
    setLocalError(null);
    try {
      await adminApi.creerAdministrateur({
        email: email.trim().toLowerCase(),
        password,
        nom_complet: nomComplet.trim(),
        niveau_autorite_id: Number(niveauId),
      });
      onCreated(`Compte administrateur créé pour ${nomComplet}.`);
    } catch (err) {
      setLocalError(err.message);
      onError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black text-slate-900">Nouvel administrateur</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <X size={18} />
          </button>
        </div>

        {localError && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-2xl text-rose-700 text-xs font-bold">
            {localError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Nom complet</label>
            <input
              type="text"
              value={nomComplet}
              onChange={(e) => setNomComplet(e.target.value)}
              className="dsi-input"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="dsi-input"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
              Mot de passe temporaire
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8 caractères minimum"
              className="dsi-input"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
              Niveau d’autorité
            </label>
            <select
              value={niveauId}
              onChange={(e) => setNiveauId(e.target.value)}
              className="dsi-input"
              required
            >
              {niveaux.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.libelle}
                </option>
              ))}
            </select>
          </div>

          <button type="submit" disabled={submitting} className="dsi-btn-primary w-full py-2.5 text-xs uppercase tracking-wider">
            {submitting ? 'Création en cours...' : 'Créer le compte'}
          </button>
        </form>
      </div>
    </div>
  );
}
